import {useState} from 'react';
import {Table, Tag, Button, Tabs} from 'antd';
import {MenuOutlined} from '@ant-design/icons';
import {useQuery, useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import {arrayMoveImmutable} from 'array-move';
import {GET_ALL_QUICK_ACTIONS, REORDER_QUICK_ACTIONS} from './resolvers';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import styles from './quickActions.module.scss';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';

const DragHandle = SortableHandle(({className}) => (
  <div className={className}>
    <MenuOutlined />
  </div>
));

const SortableItem = SortableElement(props => <tr {...props} />);
const SortableBody = SortableContainer(props => <tbody {...props} />);

const QuickActionsList = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('default');
  const [pendingReorder, setPendingReorder] = useState(null);
  const [reorderModal, setReorderModal] = useState(false);
  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.QuickActions),
  );

  const {data, loading, refetch} = useQuery(GET_ALL_QUICK_ACTIONS, {
    variables: {
      filter: {
        isDefault: activeTab === 'default',
      },
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
  });

  const [reorderQuickActions, {loading: reordering}] = useMutation(
    REORDER_QUICK_ACTIONS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const originalActions =
    data?.getAllQuickActions?.__typename === 'ResponseWithQuickActions'
      ? data?.getAllQuickActions?.data || []
      : [];

  const actions = pendingReorder || originalActions;

  const onSortEnd = ({oldIndex, newIndex}) => {
    if (oldIndex === newIndex) return;

    const currentList = pendingReorder || originalActions;
    const reordered = arrayMoveImmutable(
      currentList.slice(),
      oldIndex,
      newIndex,
    );

    setPendingReorder(reordered);
  };

  const handleConfirmReorder = () => {
    if (!pendingReorder) return;

    const positions = pendingReorder.map((action, index) => ({
      id: action.id,
      position: index + 1,
    }));

    reorderQuickActions({
      variables: {
        positions,
      },
    })
      .then(({data: {reorderQuickActions}}) => {
        if (
          reorderQuickActions.__typename === 'Error' ||
          reorderQuickActions.statusCode === 400
        ) {
          toast.error(reorderQuickActions.message);
        } else {
          toast.success('Quick actions reordered successfully.');
          setPendingReorder(null);
          setReorderModal(false);
          refetch();
        }
      })
      .catch(error => {
        toast.error(error.message);
      });
  };

  const handleCancelReorder = () => {
    setPendingReorder(null);
    setReorderModal(false);
  };

  const DraggableContainer = props => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({...restProps}) => {
    const index = actions.findIndex(x => x.id === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  const columns = [
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: 120,
      render: (_, record, index) => {
        if (activeTab === 'default') {
          return (
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <DragHandle className={styles.dragHandle} />
              <span>{pendingReorder ? index + 1 : record.position}</span>
            </div>
          );
        }
        return record.position || 'N/A';
      },
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      width: 80,
      render: icon =>
        icon ? (
          <img
            src={icon}
            alt="Icon"
            style={{width: 40, height: 40, objectFit: 'contain'}}
          />
        ) : (
          <span>—</span>
        ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      width: 100,
      render: key => <Tag>{key?.replaceAll('_', ' ') || 'N/A'}</Tag>,
    },
    ...(activeTab !== 'default'
      ? [
          {
            title: 'Category',
            dataIndex: ['category', 'name'],
            key: 'category',
            width: 120,
            render: categoryName => <Tag>{categoryName || 'N/A'}</Tag>,
          },
        ]
      : []),
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: isActive => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: date => (date ? moment(date).format('DD MMM YYYY HH:mm') : '—'),
    },
    {
      title: 'Actions',
      fixed: 'right',
      key: 'actions',
      render: (_, record) => (
        <div style={{display: 'flex', gap: '8px'}}>
          <Button
            type="link"
            onClick={() =>
              navigate(`/dashboard/quick-actions/actions/${record.id}/edit`)
            }>
            Edit
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <h2>Quick Actions</h2>
        <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
          {activeTab === 'default' && pendingReorder && (
            <>
              <Button
                type="primary"
                onClick={() => setReorderModal(true)}
                style={{background: '#52c41a', borderColor: '#52c41a'}}
                disabled={!tabOperations?.update}>
                Save Order Changes
              </Button>
              <Button onClick={handleCancelReorder}>Discard Changes</Button>
            </>
          )}
          <Button
            type="primary"
            disabled={!tabOperations?.create}
            onClick={() => navigate('/dashboard/quick-actions/actions/new')}>
            Create Quick Action
          </Button>
        </div>
      </div>
      <Tabs
        className={styles.tabs}
        activeKey={activeTab}
        onChange={key => {
          setActiveTab(key);
          setPendingReorder(null);
        }}>
        <Tabs.TabPane tab="Default (Shows in Homepage)" key="default">
          <Table
            dataSource={actions}
            columns={columns}
            className="antd-table"
            loading={loading || reordering}
            rowKey="id"
            pagination={false}
            components={{
              body: {
                wrapper: DraggableContainer,
                row: DraggableBodyRow,
              },
            }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Others" key="others">
          <Table
            dataSource={actions}
            columns={columns}
            className="antd-table"
            loading={loading}
            rowKey="id"
          />
        </Tabs.TabPane>
      </Tabs>
      <ConfirmModal
        openModal={reorderModal}
        setOpenModal={setReorderModal}
        loading={reordering}
        title="Confirm Reorder"
        confirmText="Are you sure you want to save the new order of default quick actions?"
        handleOperation={handleConfirmReorder}
      />
    </div>
  );
};

export default QuickActionsList;
